import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/vue'
import { onClickOutside } from '@vueuse/core'

import type { CSSProperties } from 'vue'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import type { TutorBillingRow } from './interfaces'

import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons'

import { navigateToTutorLegal } from '@features/tutor-billing-return'

import { TutorBillingModal } from './enums'
import { useTutorBillingStore } from './store'

interface TutorBillingRowActionsOptions {
  row: ComputedRef<TutorBillingRow>
}

interface TutorBillingRowActionsReturn {
  isOpen: Ref<boolean>
  containerRef: Ref<HTMLElement | null>
  floatingRef: Ref<HTMLElement | null>
  floatingStyles: Ref<CSSProperties>
  triggerIcon: IconDefinition
  setToggle: () => void
  setOpenEdit: () => Promise<void>
  setOpenErase: () => void
}

export const useTutorBillingRowActions = ({ row }: TutorBillingRowActionsOptions): TutorBillingRowActionsReturn => {
  const store = useTutorBillingStore()

  const isOpen = ref<boolean>(false)
  const containerRef = ref<HTMLElement | null>(null)
  const floatingRef = ref<HTMLElement | null>(null)

  const { floatingStyles } = useFloating(containerRef, floatingRef, {
    placement: 'bottom-end',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
    ],
  })

  const setClose = (): void => {
    isOpen.value = false
  }

  const setToggle = (): void => {
    isOpen.value = !isOpen.value
  }

  const setOpenEdit = async (): Promise<void> => {
    setClose()
    await navigateToTutorLegal(row.value.tutor.id)
  }

  const setOpenErase = (): void => {
    setClose()
    store.openModal(TutorBillingModal.Erase, row.value)
  }

  let stopClickOutside: (() => void) | null = null

  const setClickOutsideListener = (open: boolean): void => {
    stopClickOutside?.()
    stopClickOutside = open ? onClickOutside(containerRef, setClose) : null
  }

  watch(isOpen, setClickOutsideListener, { flush: 'sync' })

  onScopeDispose(() => setClickOutsideListener(false))

  return {
    isOpen,
    containerRef,
    floatingRef,
    floatingStyles,
    triggerIcon: faEllipsisVertical,
    setToggle,
    setOpenEdit,
    setOpenErase,
  }
}
